import * as React from "react";
import styled, { css } from "styled-components";
import { toast } from "react-toastify";

import {
  BasicModal,
  BasicModalProps
} from "src/library/components/Modal";
import { Text } from "src/library/components/Text";
import { ITerminology } from "src/core/shared/api";
import { Button } from "src/library/components/Button";
import {
  IPaymentCard,
  usePaymentApi
} from "src/modules/payment/api/payment.api";
import { IDrop } from "src/core/drops/api/drops.api";

interface MakeCardDefaultModalProps extends Omit<BasicModalProps, "title"> {
  terminology: ITerminology;
  paymentMethod: IPaymentCard;
  drop: IDrop | null;
  onUpdateSuccess?: (paymentMethod: IPaymentCard) => void;
  dropsCount?: number;
}

/**
 * Modal hanlding request to make a payment card default card for all drops
 */
const MakeCardDefaultModal: React.ReactFCWithChildren<MakeCardDefaultModalProps> = ({
  open,
  onDismiss,
  terminology,
  paymentMethod,
  drop,
  onUpdateSuccess,
  dropsCount
}) => {
  const [ updateUserBranchesDefaultPaymentRes, updateUserBranchesDefaultPaymentReq ] = usePaymentApi("UPDATE_USER_BRANCHES_DEFAULT_PAYMENT");

  React.useEffect(() => {
    if (updateUserBranchesDefaultPaymentRes.error) {
      toast.error(updateUserBranchesDefaultPaymentRes.error.message);
    }

    if (updateUserBranchesDefaultPaymentRes.data) {
      if (onUpdateSuccess) {
        onUpdateSuccess(updateUserBranchesDefaultPaymentRes.data);
      }

      toast.success("Successfully set card as default payment card.");
    }
  }, [ onUpdateSuccess, updateUserBranchesDefaultPaymentRes ]);

  const handleConfirm = React.useCallback(() => {
    if (!drop) {
      toast.error("Current drop not set. Customer drop required to update default payment method");
    }

    if (drop) {
      updateUserBranchesDefaultPaymentReq({ paymentCardId: paymentMethod.id });
    }
  }, [
    drop,
    paymentMethod.id,
    updateUserBranchesDefaultPaymentReq
  ]);

  return (
    <StyledMakeCardDefaultModal
      open={open}
      onDismiss={onDismiss}
      title="Make Card Default Payment Method"
    >
      <>
        <div className="message">
          <Text>
            {`Please confirm you would like to make card ending ${paymentMethod.last4} the default card for
            ${dropsCount ?
              ` all ${dropsCount} ${terminology.drop.plural.text}` :
              drop && `(${drop.branchNumber}) ${drop.name}` || ` current ${terminology.drop.singular.text}`}.`}
          </Text>
        </div>

        <div className="actions">
          <Button
            disabled={updateUserBranchesDefaultPaymentRes.pending}
            onClick={() => onDismiss()}
            $buttonType="secondary"
          >
            No, cancel
          </Button>

          <Button
            loading={updateUserBranchesDefaultPaymentRes.pending}
            $buttonType="primary"
            onClick={() => handleConfirm()}
          >
            Yes, make default
          </Button>
        </div>
      </>
    </StyledMakeCardDefaultModal>
  );
};

export default MakeCardDefaultModal;

const StyledMakeCardDefaultModal = styled(BasicModal)`
  ${({ theme }) => css`
    max-width: 440px;

    .message {
      margin-bottom: ${theme.spacing.margins.lg};
    }

    .actions {
      display: flex;
      justify-content: space-between;
    }
  `}
`;
